import { Helmet } from "react-helmet";
import { useState, useEffect } from "react";
import DashboardTopbar from "components/DashboardTopbar";
import DashboardMenuBar from "components/DashboardMenuBar";
import { useAppContext } from "components/Context/AppContext";
import AllocBarCanvas from "components/AllocBarCanvas";
import ConcChart from "components/ConcChart";
import { fmtDec } from "utils/format";
import {
  AreaChart, Area, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer,
} from "recharts";
import { Responsive, WidthProvider } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "styles/dashboard.css";

const DASHBOARD_API =
  process.env.REACT_APP_API_URL || "http://localhost:5050/api";

// Draggable/resizable client layout (react-grid-layout)
const ResponsiveGridLayout = WidthProvider(Responsive);
const CLIENT_LAYOUT_KEY = "trg_client_layout_v2";
const DEFAULT_CLIENT_LAYOUT = [
  { i: "narrative",  x: 0,  y: 0,  w: 10, h: 4  },
  { i: "accounts",   x: 10, y: 0,  w: 2,  h: 4  },
  { i: "portfolio",  x: 0,  y: 4,  w: 2,  h: 19 },
  { i: "risk",       x: 2,  y: 4,  w: 5,  h: 10 },
  { i: "chart",      x: 7,  y: 4,  w: 5,  h: 10 },
  { i: "spread",     x: 2,  y: 14, w: 5,  h: 9  },
  { i: "money",      x: 7,  y: 14, w: 5,  h: 9  },
];

// ── Expand icon (corner arrows, matches summary1 mockup) ──────────────────────
const ExpandIcon = (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 9 3 13 7 13" />
    <polyline points="13 7 13 3 9 3" />
    <line x1="3" y1="13" x2="7" y2="9" />
    <line x1="13" y1="3" x2="9" y2="7" />
  </svg>
);

// ── Colour helpers ────────────────────────────────────────────────────────────
function hexToRgb(h) {
  const n = parseInt(h.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
function lerpColour(a, b, t) {
  const ca = hexToRgb(a), cb = hexToRgb(b);
  return `rgb(${Math.round(ca[0] + (cb[0] - ca[0]) * t)},${Math.round(ca[1] + (cb[1] - ca[1]) * t)},${Math.round(ca[2] + (cb[2] - ca[2]) * t)})`;
}

// ── SVG Gauge ────────────────────────────────────────────────────────────────
function SvgGauge({ min = 0, max, value, target, targetLabel, colourFn, reading, readingColor }) {
  const cx = 140, cy = 138, r = 105, SEGS = 80;
  const safeVal = Number.isFinite(value) ? value : min;
  const safeMax = (Number.isFinite(max) && max > min) ? max : Math.max(safeVal * 1.5, min + 1);
  const toDeg = (v) => 180 - ((v - min) / (safeMax - min)) * 180;
  const toXY  = (deg, radius) => ({
    x: cx + radius * Math.cos((deg * Math.PI) / 180),
    y: cy - radius * Math.sin((deg * Math.PI) / 180),
  });

  const segments = [];
  for (let i = 0; i < SEGS; i++) {
    const v0 = min + (i / SEGS) * (safeMax - min);
    const v1 = min + ((i + 1) / SEGS) * (safeMax - min);
    const p0 = toXY(toDeg(v0), r), p1 = toXY(toDeg(v1), r);
    segments.push(
      <path key={i}
        d={`M ${p0.x.toFixed(2)} ${p0.y.toFixed(2)} A ${r} ${r} 0 0 1 ${p1.x.toFixed(2)} ${p1.y.toFixed(2)}`}
        fill="none" stroke={colourFn((v0 + v1) / 2)} strokeWidth="14" strokeLinecap="butt"
      />
    );
  }

  const clampedValue = Math.max(min, Math.min(safeMax, safeVal));
  const tip    = toXY(toDeg(clampedValue), r - 5);
  const ta     = toDeg(target);
  const tOuter = toXY(ta, r + 11), tInner = toXY(ta, r - 11);
  const lp     = toXY(ta, r + 24);

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <svg viewBox="0 0 280 148" width="100%"
        style={{ display: "block", overflow: "visible", maxWidth: 160 }}>
        <defs>
          <filter id="sum-nd">
            <feDropShadow dx="0" dy="1" stdDeviation="2" floodColor="rgba(0,0,0,0.5)" />
          </filter>
        </defs>
        {segments}
        <line
          x1={cx} y1={cy} x2={tip.x.toFixed(2)} y2={tip.y.toFixed(2)}
          stroke="rgba(255,255,255,0.9)" strokeWidth="2" strokeLinecap="round"
          filter="url(#sum-nd)"
        />
        <circle cx={cx} cy={cy} r="5.5" fill="var(--dash-bg)" stroke="rgba(255,255,255,0.55)" strokeWidth="1.5" />
        <circle cx={cx} cy={cy} r="2" fill="rgba(255,255,255,0.7)" />
        <line
          x1={tOuter.x.toFixed(2)} y1={tOuter.y.toFixed(2)}
          x2={tInner.x.toFixed(2)} y2={tInner.y.toFixed(2)}
          stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" strokeLinecap="round"
        />
        <text
          x={lp.x.toFixed(1)} y={lp.y.toFixed(1)}
          fontSize="16" fill="rgba(255,255,255,0.85)"
          textAnchor="middle" fontFamily="'DM Mono', monospace"
        >
          {targetLabel ?? target}
        </text>
      </svg>
      <div style={{
        fontFamily: "var(--dash-font-d)", fontSize: "1.625rem",
        color: readingColor, textAlign: "center", lineHeight: 1, marginTop: -2,
      }}>
        {reading}
      </div>
    </div>
  );
}


// ── Chart tooltip ────────────────────────────────────────────────────────────
function ChartTooltip({ active, payload, fmtMV }) {
  if (!active || !payload?.length) return null;
  const date = payload[0]?.payload?.as_of_date ?? "";
  return (
    <div style={{ background: "rgba(8,14,24,0.96)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6, padding: "7px 10px", fontSize: "0.65rem", fontFamily: "var(--dash-font-m)" }}>
      <div style={{ color: "rgba(255,255,255,0.4)", marginBottom: 4 }}>{date}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ color: "rgba(255,255,255,0.85)" }}>
          {p.name}: {fmtMV(p.value)}
        </div>
      ))}
    </div>
  );
}

// Top Risk/Hedges — 5 biggest risks + 2 biggest hedges, Risk-tab contributor format
function TopRiskBar({ data = [] }) {
  const rows   = (data || []).filter((d) => d && d.pct != null);
  const risks  = rows.filter((d) => d.pct > 0).sort((a, b) => b.pct - a.pct).slice(0, 5);
  const hedges = rows.filter((d) => d.pct < 0).sort((a, b) => a.pct - b.pct).slice(0, 2);
  const list   = [...risks, ...hedges];
  if (!list.length) return null;
  const maxAbs = Math.max(...list.map((d) => Math.abs(d.pct)), 0.0001);
  return (
    <div style={{ padding: "0.75rem 1rem 0.85rem", display: "flex", flexDirection: "column", gap: "0.45rem", flex: 1, minHeight: 0, overflowY: "auto" }}>
      {list.map((d) => {
        const isHedge  = d.pct < 0;
        const barWidth = `${(Math.abs(d.pct) / maxAbs * 100).toFixed(1)}%`;
        const barColor = isHedge ? "var(--dash-gold-text)" : "var(--dash-blue)";
        return (
          <div key={d.name} style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <div style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.6rem", fontWeight: 500, width: 140, flexShrink: 0, color: "var(--dash-text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {d.name}
            </div>
            <div style={{ flex: 1, height: 18, background: "rgba(255,255,255,0.04)", borderRadius: 3, position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: 0, left: 0, height: "100%", width: barWidth, borderRadius: 3, background: barColor, opacity: 0.85 }} />
            </div>
            <div style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.6rem", width: 38, textAlign: "right", flexShrink: 0, color: isHedge ? "var(--dash-gold-text)" : "var(--dash-text)" }}>
              {d.pct > 0 ? "+" : ""}{fmtDec(d.pct)}%
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Broker dot colours for the accounts table ────────────────────────────────
const BROKER_COLORS = {
  "Interactive Brokers": "#4e8fdd",
  "Fidelity":            "#22d47e",
  "Charles Schwab":      "#C9A84C",
  "Goldman Sachs":       "#b87bc8",
  "Morgan Stanley":      "#e05252",
};
const DEFAULT_BROKER_COLOR = "#5a7090";

// ── Consolidated Accounts table columns ──────────────────────────────────────
const BROKER_COLS = [
  { key: "broker",        label: "Broker",      type: "text", width: "22%", align: "left"  },
  { key: "brokerAccount", label: "Broker Acct", type: "text", width: "22%", align: "left"  },
  { key: "marketValue",   label: "Mkt Val",     type: "num",  width: "18%", align: "right" },
  { key: "dayReturn",     label: "1D Ret",      type: "num",  width: "18%", align: "right" },
  { key: "var1d",         label: "VaR",         type: "num",  width: "20%", align: "right" },
];

// ── Sortable Consolidated Accounts table ─────────────────────────────────────
function BrokerTable({ rows, totalMV, totalVar, fmtVar, marginTop }) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("desc");

  const onSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      const col = BROKER_COLS.find(c => c.key === key);
      setSortDir(col?.type === "num" ? "desc" : "asc");
      setSortKey(key);
    }
  };

  const sortedRows = (() => {
    if (!sortKey) return rows;
    const col = BROKER_COLS.find(c => c.key === sortKey);
    const mul = sortDir === "asc" ? 1 : -1;
    return rows.slice().sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (col?.type === "num") return (av - bv) * mul;
      return String(av).localeCompare(String(bv)) * mul;
    });
  })();

  return (
    <table className="trg-acct-table" style={marginTop ? { marginTop } : undefined}>
      <thead>
        <tr>
          {BROKER_COLS.map(col => (
            <th
              key={col.key}
              style={{ width: col.width, textAlign: col.align, cursor: "pointer", userSelect: "none" }}
              title="Click to sort"
              onClick={() => onSort(col.key)}
            >
              {col.label}
              <span style={{ marginLeft: 3, opacity: sortKey === col.key ? 1 : 0.25, fontSize: "0.85em" }}>
                {sortKey === col.key ? (sortDir === "asc" ? "▲" : "▼") : "▼"}
              </span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sortedRows.map((b, i) => {
          const dot = BROKER_COLORS[b.broker] || DEFAULT_BROKER_COLOR;
          const rc = b.dayReturn == null ? "var(--dash-muted)" : b.dayReturn >= 0 ? "var(--dash-green)" : "var(--dash-red)";
          return (
            <tr key={i}>
              <td>
                <div className="trg-broker-cell">
                  <div className="trg-broker-dot" style={{ background: dot, boxShadow: `0 0 5px ${dot}88` }} />
                  <span style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.76rem", fontWeight: 500, color: "var(--dash-text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {b.broker}
                  </span>
                </div>
              </td>
              <td style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.75rem", color: "var(--dash-text2)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {b.brokerAccount ?? "—"}
              </td>
              <td style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.75rem", color: "var(--dash-text)" }}>
                {b.marketValue != null ? `${(b.marketValue / 1e6).toFixed(1)}M` : "—"}
              </td>
              <td style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.75rem", color: rc }}>
                {b.dayReturn != null ? `${b.dayReturn >= 0 ? "+" : ""}${fmtDec(b.dayReturn)}%` : "—"}
              </td>
              <td style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.75rem", color: "var(--dash-text)" }}>
                {fmtVar(b.var1d)}
              </td>
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr>
          <td colSpan={2} style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.6rem", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--dash-text)" }}>
            Total
          </td>
          <td style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.78rem", fontWeight: 500, color: "var(--dash-text)" }}>
            {totalMV > 0 ? `${(totalMV / 1e6).toFixed(1)}M` : "—"}
          </td>
          <td />
          <td style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.78rem", fontWeight: 500, color: "var(--dash-text)" }}>
            {totalVar > 0 ? fmtVar(totalVar) : "—"}
          </td>
        </tr>
      </tfoot>
    </table>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function SummaryPage() {
  const { selectedAccount } = useAppContext();
  const [theme, setTheme]             = useState(() => localStorage.getItem("trg_theme") || "dark");
  const [activeRange, setActiveRange] = useState("1Y");
  const [metrics, setMetrics]         = useState({});
  const [, setAsOfDate]       = useState(null);
  const [chartPoints, setChartPoints] = useState([]);
  const [bmkName,     setBmkName]     = useState(null);
  const [brokerRows, setBrokerRows]   = useState([]);
  const [concRows, setConcRows]       = useState([]);
  const [, setTopRiskData] = useState([]);
  const [riskContribs, setRiskContribs] = useState([]); // /risk/contributions (same data as Risk tab)
  const [, setGaugeData]     = useState(null);
  const [riskSummary, setRiskSummary] = useState(null);   // /risk/summary → same gauge limits as Risk tab
  const [worstScenario, setWorstScenario] = useState(null); // worst stress scenario (client "worst-case loss")
  const [clientLayout, setClientLayout] = useState(() => {
    try { const s = localStorage.getItem(CLIENT_LAYOUT_KEY); if (s) return JSON.parse(s); } catch (e) {}
    return DEFAULT_CLIENT_LAYOUT;
  });
  const onClientLayoutChange = (l) => {
    setClientLayout(l);
    try { localStorage.setItem(CLIENT_LAYOUT_KEY, JSON.stringify(l)); } catch (e) {}
  };
  const [allocLevels, setAllocLevels] = useState(null);   // all drill-down levels, loaded once
  const [allocCurrentLevel, setAllocCurrentLevel] = useState("all");
  const [fsPanel, setFsPanel]         = useState(null);   // { title, content }
  const [view, setView]               = useState("advisor"); // "advisor" | "client"
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 550px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 550px)");
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // ── Fetch on account change ──
  useEffect(() => {
    if (!selectedAccount) return;
    const token  = sessionStorage.getItem("token");
    const params = `account_id=${selectedAccount.account_id}&token=${token}`;

    fetch(`${DASHBOARD_API}/summary/metrics?${params}`)
      .then((r) => r.json())
      .then((d) => { setMetrics(d); setAsOfDate(d.asOfDate || null); })
      .catch(console.error);

    fetch(`${DASHBOARD_API}/summary/brokers?${params}`)
      .then((r) => r.json())
      .then((d) => { if (Array.isArray(d)) setBrokerRows(d); })
      .catch(console.error);

    fetch(`${DASHBOARD_API}/summary/concentrations?${params}`)
      .then((r) => r.json())
      .then((d) => { if (Array.isArray(d)) setConcRows(d); })
      .catch(console.error);

    fetch(`${DASHBOARD_API}/summary/gauges?${params}`)
      .then((r) => r.json())
      .then(setGaugeData)
      .catch(console.error);

    fetch(`${DASHBOARD_API}/summary/allocation?${params}`)
      .then((r) => r.json())
      .then(setAllocLevels)
      .catch(console.error);

    fetch(`${DASHBOARD_API}/summary/top_risk?${params}`)
      .then((r) => r.json())
      .then((d) => { if (Array.isArray(d)) setTopRiskData(d); })
      .catch(console.error);

    // Same gauge limits as the Risk tab
    fetch(`${DASHBOARD_API}/risk/summary?${params}`)
      .then((r) => r.json())
      .then((d) => setRiskSummary(d && d.measures ? d : null))
      .catch(console.error);

    // Risk contributors (same source as the Risk tab) → advisor Top Risk/Hedges
    fetch(`${DASHBOARD_API}/risk/contributions?${params}`)
      .then((r) => r.json())
      .then((d) => { if (Array.isArray(d)) setRiskContribs(d); })
      .catch(console.error);

    // Worst stress scenario → client "Worst-case loss"
    fetch(`${DASHBOARD_API}/stress/scenarios?${params}`)
      .then((r) => r.json())
      .then((d) => {
        setWorstScenario(
          Array.isArray(d) && d.length
            ? d.reduce((a, b) => (a.impactUSD < b.impactUSD ? a : b))
            : null
        );
      })
      .catch(console.error);
  }, [selectedAccount]);

  // ── Fetch chart on range / account change ──
  useEffect(() => {
    if (!selectedAccount) return;
    const token  = sessionStorage.getItem("token");
    const params = `account_id=${selectedAccount.account_id}&token=${token}`;
    fetch(`${DASHBOARD_API}/summary/chart/${activeRange}?${params}`)
      .then((r) => r.json())
      .then((data) => {
        const pts = data?.points;
        if (!Array.isArray(pts)) return;
        setBmkName(data.benchmark_name ?? null);
        setChartPoints(pts.map((p) => ({
          label:      p.label,
          as_of_date: p.as_of_date,
          value:      p.value != null ? p.value / 1e6 : null,
          bmk:        p.bmk   != null ? p.bmk   / 1e6 : null,
        })));
      })
      .catch(console.error);
  }, [activeRange, selectedAccount]);

  // ── Helpers ──────────────────────────────────────────────────────────────────
  // Chart scale: use K when max portfolio value is below 1.5M (values already in M units)
  const chartMaxVal = chartPoints.length ? Math.max(...chartPoints.map(p => p.value ?? 0)) : 0;
  const chartUseK   = chartMaxVal < 1.5;
  const fmtMV = (v) => v == null ? "—" : chartUseK ? `${(v * 1000).toFixed(0)}K` : `${v.toFixed(1)}M`;
  const fmtVar = (v) => v == null ? "—" : Math.abs(v) >= 1.5e6 ? `${(v / 1e6).toFixed(1)}M` : `${(v / 1e3).toFixed(0)}K`;

  const aum         = metrics.aum;
  const unrealized  = metrics.unrealizedGain;
  const dayReturn   = metrics.dayReturn;
  const ytdReturn   = metrics.ytdReturn;
  const riskMetric  = metrics.riskMetric;
  const volatility  = metrics.volatility;
  const beta        = metrics.beta;

  // ── Gauges: same data & limits as the Risk tab (/risk/summary) ──
  const gaugeRisk      = riskSummary?.gaugeRisk;
  const gaugeSharpeVar = riskSummary?.gaugeSharpeVar;

  // VaR vs Limit
  const riskMax    = gaugeRisk ? Math.max(gaugeRisk.value, gaugeRisk.limit) * 1.15 : 30;
  const riskColour = (v) => {
    if (!gaugeRisk) return "#C9A84C";
    const { limit, band } = gaugeRisk;
    const p = v / riskMax;
    const pY = (limit - band) / riskMax, pR = (limit + band) / riskMax;
    if (p <= pY) return lerpColour("#22d47e", "#C9A84C", Math.min(1, p / pY));
    if (p <= pR) return lerpColour("#C9A84C", "#e05252", (p - pY) / (pR - pY));
    return "#e05252";
  };
  const varDiff      = gaugeRisk ? gaugeRisk.value - gaugeRisk.limit : 0;
  const varReadColor = gaugeRisk
    ? (varDiff < -gaugeRisk.band ? "#22d47e" : varDiff < gaugeRisk.band ? "#C9A84C" : "#e05252")
    : "#C9A84C";

  // SR (VaR) vs BMK
  const sharpeVarColour = (v) => {
    if (!gaugeSharpeVar) return "#C9A84C";
    const { target, band, max } = gaugeSharpeVar;
    const p = v / max;
    const lo = (target - band) / max, hi = (target + band) / max;
    if (p <= lo) return "#e05252";
    if (p <= hi) return lerpColour("#e05252", "#C9A84C", (p - lo) / (hi - lo));
    return lerpColour("#C9A84C", "#22d47e", Math.min(1, (p - hi) / (1 - hi)));
  };
  const sharpeVarDiff      = gaugeSharpeVar ? gaugeSharpeVar.value - gaugeSharpeVar.target : 0;
  const sharpeVarReadColor = gaugeSharpeVar
    ? (sharpeVarDiff > gaugeSharpeVar.band ? "#22d47e" : sharpeVarDiff > -gaugeSharpeVar.band ? "#C9A84C" : "#e05252")
    : "#C9A84C";

  // Total row for accounts table
  const totalMV  = brokerRows.reduce((s, b) => s + (b.marketValue  || 0), 0);
  const totalVar = brokerRows.reduce((s, b) => s + (b.var1d        || 0), 0);

  // Fullscreen
  const openFs = (title, content) => setFsPanel({ title, content });
  const closeFs = () => setFsPanel(null);

  // ── Client view: plain-English concentration labels (real data, relabeled) ──
  const CLIENT_CONC = {
    "Asset Class": "By type", "Region": "By region", "Currency": "By currency",
    "Industry": "By sector", "Single Name": "One company",
  };
  const clientConcRows = concRows.map((d) => ({ ...d, category: CLIENT_CONC[d.category] ?? d.category }));

  // ── Client narrative: performance, risk-adjusted vs. market, then limit breaches ──
  const rawRiskLevel   = (typeof window !== "undefined" && localStorage.getItem("trg_risk_level")) || "custom";
  const toleranceLabel = rawRiskLevel === "custom"
    ? "Aggressive"
    : rawRiskLevel.charAt(0).toUpperCase() + rawRiskLevel.slice(1);

  // Performance + risk-adjusted-return-vs-market
  let perfSentence = "";
  if (ytdReturn != null) {
    let rar = "";
    if (gaugeSharpeVar) {
      const diff = gaugeSharpeVar.value - gaugeSharpeVar.target;
      const band = gaugeSharpeVar.band ?? 0;
      const how  = diff > band ? "ahead of" : diff > -band ? "in line with" : "behind";
      rar = `, and for the risk you're taking your returns are ${how} the market`;
    }
    perfSentence = `You're ${ytdReturn >= 0 ? "up" : "down"} ${fmtDec(Math.abs(ytdReturn))}% so far this year${rar}.`;
  }

  // Areas beyond their limits
  const CONC_PHRASE = {
    "Asset Class": "one asset type",
    "Region":      "one region",
    "Currency":    "one currency",
    "Industry":    "one sector",
    "Single Name": "a single company",
  };
  const narrRiskRatio = gaugeRisk && gaugeRisk.limit ? gaugeRisk.value / gaugeRisk.limit : null;
  const overItems = [];
  if (narrRiskRatio != null && narrRiskRatio > 1.0) overItems.push("your overall risk");
  concRows.forEach((d) => {
    if (d.limit_value && d.max_weight != null && d.max_weight / d.limit_value > 1.05) {
      overItems.push(`your concentration in ${CONC_PHRASE[d.category] ?? "one area"}`);
    }
  });
  const joinList = (arr) => arr.length <= 1
    ? (arr[0] || "")
    : arr.slice(0, -1).join(", ") + ", and " + arr[arr.length - 1];

  let limitsSentence, narrColor;
  if (overItems.length > 0) {
    narrColor = "#e05252";
    limitsSentence = overItems.length === 1
      ? `Right now, ${overItems[0]} is beyond its limit — a good time to talk with your advisor.`
      : `Right now a few areas are beyond their limits — ${joinList(overItems)} — so this is a good time to talk with your advisor.`;
  } else if (narrRiskRatio != null && narrRiskRatio > 0.9) {
    narrColor = "#C9A84C";
    limitsSentence = "Everything is within your limits, though overall risk is getting close to its limit — worth keeping an eye on.";
  } else {
    narrColor = narrRiskRatio == null ? "var(--dash-muted)" : "#22d47e";
    limitsSentence = "Everything is comfortably within your limits.";
  }

  const NARR_BG = { "#e05252": "rgba(224,82,82,0.10)", "#C9A84C": "rgba(201,168,76,0.10)", "#22d47e": "rgba(34,212,126,0.08)" };
  const narrBg = NARR_BG[narrColor] || "var(--dash-surface, #0f1a2a)";
  const narrText = [perfSentence, limitsSentence].filter(Boolean).join(" ");

  const clientNarrative = (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", gap: "0.6rem", padding: "0.9rem 1.15rem", background: narrBg, border: "1px solid var(--dash-border)", borderLeft: `4px solid ${narrColor}`, borderRadius: 10 }}>
      <div className="trg-card-title">Where You Stand</div>
      <span style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.92rem", lineHeight: 1.55 }}>
        <strong style={{ color: "var(--dash-gold-text, #d4b06a)", fontWeight: 700 }}>{toleranceLabel} risk profile.</strong>
        <span style={{ color: "var(--dash-text)" }}> {narrText}</span>
      </span>
    </div>
  );

  // ── Advisor/Client view toggle control ──
  const viewToggle = (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 9, padding: "0 0 0.6rem" }}>
      <span style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.6rem", fontWeight: 600, letterSpacing: "0.11em", textTransform: "uppercase", color: "var(--dash-muted2)" }}>View</span>
      <div style={{ display: "inline-flex", border: "1px solid var(--dash-border2)", borderRadius: 7, overflow: "hidden", background: "var(--dash-bg3, #0d1626)" }}>
        {["advisor", "client"].map((v) => (
          <button
            key={v}
            onClick={() => setView(v)}
            style={{
              fontFamily: "var(--dash-font-b)", fontSize: "0.6rem", fontWeight: 600,
              letterSpacing: "0.06em", textTransform: "uppercase", padding: "0.32rem 0.95rem",
              border: "none", cursor: "pointer", background: view === v ? "rgba(201,168,76,0.16)" : "transparent",
              color: view === v ? "var(--dash-gold-text, #d4b06a)" : "var(--dash-text2)",
            }}
          >
            {v}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>The Big Picture – Tail Risk Global</title>
      </Helmet>

      <div className={`trg-shell trg-summary${theme === "light" ? " theme-light" : ""}`}>

        {/* Top bar */}
        <DashboardTopbar />

        {/* Tab bar */}
        <DashboardMenuBar onThemeChange={setTheme} />

        {/* Body */}
        <main className="trg-body">

          {viewToggle}

          {/* 4-col grid — ADVISOR (unchanged) */}
          {view === "advisor" && (
          <div className="trg-main-grid">

            {/* ── COL 1: Performance ────────────────────────────────────── */}
            <div className="trg-card trg-perf-col">
              <div className="trg-card-header">
                <div className="trg-card-title">Performance</div>
              </div>
              <div className="trg-perf-metrics">
                <div className="trg-metric-row">
                  <div className="trg-metric-label">Market Value</div>
                  <div className="trg-metric-value">
                    {aum != null ? (aum / 1e6).toFixed(1) : "—"}
                    <span className="unit">M</span>
                  </div>
                </div>
                <div className="trg-metric-row">
                  <div className="trg-metric-label">Unrealized Gain</div>
                  <div className={`trg-metric-value${unrealized != null ? (unrealized >= 0 ? " green" : " red") : ""}`}>
                    {unrealized != null ? `${unrealized >= 0 ? "+" : ""}${(unrealized / 1e6).toFixed(1)}` : "—"}
                    {unrealized != null && <span className="unit">M</span>}
                  </div>
                  <div className="trg-metric-sub">since inception</div>
                </div>
                <div className="trg-metric-row">
                  <div className="trg-metric-label">Today's Return</div>
                  <div className={`trg-metric-value${dayReturn != null ? (dayReturn >= 0 ? " green" : " red") : ""}`}>
                    {dayReturn != null ? `${dayReturn >= 0 ? "+" : ""}${fmtDec(dayReturn)}%` : "—"}
                  </div>
                  <div className="trg-metric-sub">vs prior close</div>
                </div>
                <div className="trg-metric-row">
                  <div className="trg-metric-label">YTD Return</div>
                  <div className={`trg-metric-value${ytdReturn != null ? (ytdReturn >= 0 ? " green" : " red") : ""}`}>
                    {ytdReturn != null ? `${ytdReturn >= 0 ? "+" : ""}${fmtDec(ytdReturn)}%` : "—"}
                  </div>
                  <div className="trg-metric-sub">year to date</div>
                </div>
              </div>
            </div>

            {/* ── COL 2: Risk ───────────────────────────────────────────── */}
            <div className="trg-card trg-risk-col">
              <div className="trg-card-header">
                <div className="trg-card-title">Risk</div>
                <div style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.55rem", color: "var(--dash-muted2)" }}>USD</div>
              </div>

              {/* Risk strip */}
              <div className="trg-risk-strip">
                <div className="trg-risk-strip-item">
                  <div className="trg-risk-strip-label">{riskMetric?.label ?? "—"}</div>
                  <div className="trg-risk-strip-value" style={{ color: "#e05252" }}>
                    {riskMetric?.value != null
                      ? Math.abs(riskMetric.value) >= 10e6
                        ? (riskMetric.value / 1e6).toFixed(0)
                        : Math.abs(riskMetric.value) >= 1e6
                          ? (riskMetric.value / 1e6).toFixed(1)
                          : (riskMetric.value / 1e3).toFixed(1)
                      : "—"}
                    <span style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.938rem", color: "var(--dash-muted)", marginLeft: 2 }}>
                      {riskMetric?.value != null ? (Math.abs(riskMetric.value) >= 1e6 ? "M" : "K") : ""}
                    </span>
                  </div>
                </div>
                <div className="trg-risk-strip-item">
                  <div className="trg-risk-strip-label">Volatility</div>
                  <div className="trg-risk-strip-value">
                    {volatility != null ? fmtDec(volatility) : "—"}
                    <span style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.938rem", color: "var(--dash-muted)", marginLeft: 2 }}>%</span>
                  </div>
                  <div className="trg-risk-strip-sub">annualised</div>
                </div>
                <div className="trg-risk-strip-item">
                  <div className="trg-risk-strip-label">Beta</div>
                  <div className="trg-risk-strip-value">
                    {beta != null ? fmtDec(beta) : "—"}
                  </div>
                  <div className="trg-risk-strip-sub">vs S&P 500</div>
                </div>
              </div>

              {/* Two gauges */}
              <div className="trg-gauges-row">
                <div className="trg-gauge-section">
                  <div className="trg-section-label">VAR 95% vs Limit</div>
                  {gaugeRisk && (
                  <SvgGauge
                    min={0} max={riskMax}
                    value={gaugeRisk.value}
                    target={gaugeRisk.limit}
                    targetLabel={gaugeRisk.targetLabel ?? `${gaugeRisk.limit.toFixed(0)}M`}
                    colourFn={riskColour}
                    reading={
                      <>{gaugeRisk.readingValue ?? gaugeRisk.value.toFixed(1)}<span style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.938rem", color: "var(--dash-muted)", marginLeft: 2 }}>{gaugeRisk.readingUnit ?? "M"}</span></>
                    }
                    readingColor={varReadColor}
                  />
                  )}
                </div>
                <div className="trg-gauge-section">
                  <div className="trg-section-label">SR (VaR) vs BMK</div>
                  {gaugeSharpeVar && (
                  <SvgGauge
                    min={0} max={gaugeSharpeVar.max}
                    value={gaugeSharpeVar.value}
                    target={gaugeSharpeVar.target}
                    targetLabel={fmtDec(gaugeSharpeVar.target)}
                    colourFn={sharpeVarColour}
                    reading={fmtDec(gaugeSharpeVar.value)}
                    readingColor={sharpeVarReadColor}
                  />
                  )}
                </div>
              </div>

              <div className="trg-gauge-note">
                Both gauges use VaR 95% 1D
              </div>

              {/* Concentrations vs Limits */}
              <div className="trg-conc-section">
                <div className="trg-conc-header">
                  <div className="trg-conc-title">Concentrations vs Limits</div>
                  <button className="trg-expand-btn" onClick={() =>
                    openFs("Concentrations vs Limits", <ConcChart rows={concRows} />)
                  }>{ExpandIcon}</button>
                </div>
                <ConcChart rows={concRows} />
              </div>
            </div>

            {/* ── COL 3: Allocation ─────────────────────────────────────── */}
            <div className="trg-alloc-col-wrap trg-xpand-host">
              <button className="trg-expand-btn trg-expand-btn--corner" onClick={() =>
                openFs("Asset Allocation vs VaR Contribution",
                  <AllocBarCanvas
                    levels={allocLevels}
                    theme={theme}
                    initialLevel={allocCurrentLevel}
                  />
                )
              }>{ExpandIcon}</button>
              <div className="trg-card-header">
                <div className="trg-card-title">Allocation</div>
              </div>

              {/* Asset Allocation vs VaR Contribution */}
              <div className="trg-alloc-body">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
                  <div className="trg-alloc-name">Asset Allocation vs VaR Contribution</div>
                </div>
                <AllocBarCanvas
                  levels={allocLevels}
                  theme={theme}
                  onLevelChange={setAllocCurrentLevel}
                />
              </div>

              <div style={{ height: 1, background: "var(--dash-border)", flexShrink: 0 }} />

              {/* Top Risk Contributors/Hedges */}
              <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                <div style={{ padding: "0.65rem 1rem 0.3rem", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
                  <div className="trg-alloc-name">Top Risk/Hedges — % VaR Contribution</div>
                  <button className="trg-expand-btn" onClick={() =>
                    openFs("Top Risk Contributors/Hedges — % VaR Contribution",
                      <div style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, justifyContent: "center" }}>
                        <TopRiskBar data={riskContribs} />
                      </div>
                    )
                  }>{ExpandIcon}</button>
                </div>
                <TopRiskBar data={riskContribs} />
              </div>
            </div>

            {/* ── COL 4: Chart + Table ──────────────────────────────────── */}
            <div className="trg-right-col">

              {/* Historical Market Value */}
              <div className="trg-card trg-chart-card trg-xpand-host">
                <div className="trg-chart-inner">
                  <div className="trg-chart-top">
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div className="trg-card-title">Historical Market Value</div>
                      <button className="trg-expand-btn trg-expand-btn--corner" onClick={() =>
                        openFs("Historical Market Value",
                          <div style={{ flex: 1, minHeight: 400, display: "flex", flexDirection: "column" }}>
                            <div style={{ position: "relative", flex: 1, minHeight: 0 }}>
                            <div style={{ position: "absolute", inset: 0 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <AreaChart data={chartPoints} margin={{ top: 6, right: 10, left: 0, bottom: 0 }}>
                                <defs>
                                  <linearGradient id="areaGradFs" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="var(--dash-blue)" stopOpacity={0.16} />
                                    <stop offset="100%" stopColor="var(--dash-blue)" stopOpacity={0} />
                                  </linearGradient>
                                </defs>
                                <CartesianGrid stroke="rgba(255,255,255,0.04)" strokeDasharray="3 3" vertical={false} />
                                <XAxis
                                  dataKey="as_of_date"
                                  tickFormatter={(v, i) => chartPoints[i]?.label ?? v}
                                  tick={{ fill: "rgba(255,255,255,0.25)", fontSize: 8, fontFamily: "var(--dash-font-m)" }}
                                  axisLine={false} tickLine={false}
                                  interval="preserveStartEnd"
                                />
                                <YAxis
                                  tickFormatter={(v) => fmtMV(v)}
                                  tick={{ fill: "rgba(255,255,255,0.25)", fontSize: 8, fontFamily: "var(--dash-font-m)" }}
                                  axisLine={false} tickLine={false}
                                  width={40} domain={["auto", "auto"]}
                                />
                                <Tooltip content={(props) => <ChartTooltip {...props} fmtMV={fmtMV} />} />
                                <Area
                                  type="monotone" dataKey="value"
                                  stroke="var(--dash-blue)" strokeWidth={1.8}
                                  fill="url(#areaGradFs)" dot={false}
                                  activeDot={{ r: 3, fill: "var(--dash-blue)" }}
                                />
                                <Line
                                  type="monotone" dataKey="bmk" name={bmkName ?? "Benchmark"}
                                  stroke="var(--dash-gold)" strokeWidth={1.5}
                                  strokeDasharray="5 4" dot={false}
                                  activeDot={{ r: 3, fill: "var(--dash-gold)" }}
                                />
                              </AreaChart>
                            </ResponsiveContainer>
                            </div>
                            </div>
                          </div>
                        )
                      }>{ExpandIcon}</button>
                    </div>
                    <div className="trg-period-btns" style={{ marginRight: 28 }}>
                      {["1M", "3M", "1Y", "3Y", "ALL"].map((r) => (
                        <button
                          key={r}
                          className={`trg-period-btn${activeRange === r ? " active" : ""}`}
                          onClick={() => setActiveRange(r)}
                        >
                          {r}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="trg-chart-legend">
                    <div className="trg-legend-item">
                      <div style={{ width: 16, height: 2, background: "var(--dash-blue)", borderRadius: 1 }} />
                      Portfolio
                    </div>
                    {bmkName && (
                      <div className="trg-legend-item">
                        <div style={{ width: 16, height: 0, borderTop: "2px dashed var(--dash-gold)" }} />
                        {bmkName}
                      </div>
                    )}
                  </div>

                  <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
                    <div style={{ position: "absolute", inset: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartPoints} margin={{ top: 6, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="var(--dash-blue)" stopOpacity={0.16} />
                            <stop offset="100%" stopColor="var(--dash-blue)" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid stroke="rgba(255,255,255,0.04)" strokeDasharray="3 3" vertical={false} />
                        <XAxis
                          dataKey="as_of_date"
                          tickFormatter={(v, i) => chartPoints[i]?.label ?? v}
                          tick={{ fill: "rgba(255,255,255,0.25)", fontSize: 8, fontFamily: "var(--dash-font-m)" }}
                          axisLine={false} tickLine={false}
                          interval="preserveStartEnd"
                        />
                        <YAxis
                          tickFormatter={(v) => `${v}M`}
                          tick={{ fill: "rgba(255,255,255,0.25)", fontSize: 8, fontFamily: "var(--dash-font-m)" }}
                          axisLine={false} tickLine={false}
                          width={40} domain={["auto", "auto"]}
                        />
                        <Tooltip content={(props) => <ChartTooltip {...props} fmtMV={fmtMV} />} />
                        <Area
                          type="monotone" dataKey="value"
                          stroke="var(--dash-blue)" strokeWidth={1.8}
                          fill="url(#areaGrad)" dot={false}
                          activeDot={{ r: 3, fill: "var(--dash-blue)" }}
                        />
                        <Line
                          type="monotone" dataKey="bmk" name={bmkName ?? "Benchmark"}
                          stroke="var(--dash-gold)" strokeWidth={1.5}
                          strokeDasharray="5 4" dot={false}
                          activeDot={{ r: 3, fill: "var(--dash-gold)" }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>

              {/* Consolidated Accounts table */}
              <div className="trg-card trg-table-card">
                <div className="trg-table-inner">
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div className="trg-card-title">Consolidated Accounts</div>
                    <button className="trg-expand-btn" onClick={() =>
                      openFs("Consolidated Accounts",
                        <BrokerTable rows={brokerRows} totalMV={totalMV} totalVar={totalVar} fmtVar={fmtVar} marginTop={8} />
                      )
                    }>{ExpandIcon}</button>
                  </div>
                  <BrokerTable rows={brokerRows} totalMV={totalMV} totalVar={totalVar} fmtVar={fmtVar} />
                </div>
              </div>

            </div>{/* /right-col */}

          </div>
          )}{/* /main-grid ADVISOR */}

          {/* CLIENT view — two-row layout */}
          {view === "client" && (() => {
          const clientTiles = [
            /* Narrative */
            <div key="narrative" className="trg-tile">{clientNarrative}</div>,

            /* Your Portfolio */
            <div key="portfolio" className="trg-tile"><div className="trg-card trg-perf-col" style={{ height: "100%" }}>
              <div className="trg-card-header">
                <div className="trg-card-title">Your Portfolio</div>
              </div>
              <div className="trg-perf-metrics">
                <div className="trg-metric-row">
                  <div className="trg-metric-label">Total Value</div>
                  <div className="trg-metric-value">
                    {aum != null ? (aum / 1e6).toFixed(1) : "—"}
                    <span className="unit">M</span>
                  </div>
                </div>
                <div className="trg-metric-row">
                  <div className="trg-metric-label">Total Gain</div>
                  <div className={`trg-metric-value${unrealized != null ? (unrealized >= 0 ? " green" : " red") : ""}`}>
                    {unrealized != null ? `${unrealized >= 0 ? "+" : ""}${(unrealized / 1e6).toFixed(1)}` : "—"}
                    {unrealized != null && <span className="unit">M</span>}
                  </div>
                </div>
                <div className="trg-metric-row">
                  <div className="trg-metric-label">Today</div>
                  <div className={`trg-metric-value${dayReturn != null ? (dayReturn >= 0 ? " green" : " red") : ""}`}>
                    {dayReturn != null ? `${dayReturn >= 0 ? "+" : ""}${fmtDec(dayReturn)}%` : "—"}
                  </div>
                </div>
                <div className="trg-metric-row">
                  <div className="trg-metric-label">This Year</div>
                  <div className={`trg-metric-value${ytdReturn != null ? (ytdReturn >= 0 ? " green" : " red") : ""}`}>
                    {ytdReturn != null ? `${ytdReturn >= 0 ? "+" : ""}${fmtDec(ytdReturn)}%` : "—"}
                  </div>
                </div>
              </div>
            </div></div>,

            /* Risk Check */
            <div key="risk" className="trg-tile"><div className="trg-card trg-risk-col" style={{ height: "100%" }}>
              <div className="trg-card-header">
                <div className="trg-card-title">Risk Check</div>
                <div style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.55rem", color: "var(--dash-muted2)" }}>USD</div>
              </div>

              <div className="trg-risk-strip">
                <div className="trg-risk-strip-item">
                  <div className="trg-risk-strip-label">Bad-day loss</div>
                  <div className="trg-risk-strip-value" style={{ color: "#e05252" }}>
                    {riskMetric?.value != null
                      ? Math.abs(riskMetric.value) >= 10e6
                        ? (riskMetric.value / 1e6).toFixed(0)
                        : Math.abs(riskMetric.value) >= 1e6
                          ? (riskMetric.value / 1e6).toFixed(1)
                          : (riskMetric.value / 1e3).toFixed(1)
                      : "—"}
                    <span style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.938rem", color: "var(--dash-muted)", marginLeft: 2 }}>
                      {riskMetric?.value != null ? (Math.abs(riskMetric.value) >= 1e6 ? "M" : "K") : ""}
                    </span>
                  </div>
                </div>
                <div className="trg-risk-strip-item">
                  <div className="trg-risk-strip-label">Typical swing</div>
                  <div className="trg-risk-strip-value">
                    {volatility != null ? fmtDec(volatility) : "—"}
                    <span style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.938rem", color: "var(--dash-muted)", marginLeft: 2 }}>%</span>
                  </div>
                </div>
                <div className="trg-risk-strip-item">
                  <div className="trg-risk-strip-label">Moves vs market</div>
                  <div className="trg-risk-strip-value">{beta != null ? fmtDec(beta) : "—"}</div>
                </div>
              </div>

              {worstScenario && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "0.55rem 1rem", background: "rgba(224,82,82,0.06)", borderBottom: "1px solid var(--dash-border)" }}>
                  <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                    <span style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.662rem", fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--dash-muted)" }}>Worst-case loss</span>
                    <span style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.65rem", color: "var(--dash-text2)", marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{worstScenario.name}</span>
                  </div>
                  <div style={{ fontFamily: "var(--dash-font-d)", fontSize: "1.938rem", color: "#e05252", lineHeight: 1, whiteSpace: "nowrap", flexShrink: 0 }}>
                    {Math.abs(worstScenario.impactUSD) >= 1 ? Math.abs(worstScenario.impactUSD).toFixed(1) : (Math.abs(worstScenario.impactUSD) * 1000).toFixed(0)}
                    <span style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.938rem", color: "var(--dash-muted)", marginLeft: 2 }}>{Math.abs(worstScenario.impactUSD) >= 1 ? "M" : "K"}</span>
                  </div>
                </div>
              )}

              <div className="trg-gauges-row">
                <div className="trg-gauge-section">
                  <div className="trg-section-label">Risk used vs your limit</div>
                  {gaugeRisk && (
                  <SvgGauge
                    min={0} max={riskMax}
                    value={gaugeRisk.value}
                    target={gaugeRisk.limit}
                    targetLabel={gaugeRisk.targetLabel ?? `${gaugeRisk.limit.toFixed(0)}M`}
                    colourFn={riskColour}
                    reading={
                      <>{gaugeRisk.readingValue ?? gaugeRisk.value.toFixed(1)}<span style={{ fontFamily: "var(--dash-font-b)", fontSize: "0.938rem", color: "var(--dash-muted)", marginLeft: 2 }}>{gaugeRisk.readingUnit ?? "M"}</span></>
                    }
                    readingColor={varReadColor}
                  />
                  )}
                </div>
                <div className="trg-gauge-section">
                  <div className="trg-section-label">Reward for the risk taken vs Market</div>
                  {gaugeSharpeVar && (
                  <SvgGauge
                    min={0} max={gaugeSharpeVar.max}
                    value={gaugeSharpeVar.value}
                    target={gaugeSharpeVar.target}
                    targetLabel={fmtDec(gaugeSharpeVar.target)}
                    colourFn={sharpeVarColour}
                    reading={fmtDec(gaugeSharpeVar.value)}
                    readingColor={sharpeVarReadColor}
                  />
                  )}
                </div>
              </div>

            </div></div>,

            /* Value Over Time */
            <div key="chart" className="trg-tile"><div className="trg-card trg-chart-card" style={{ height: "100%" }}>
                <div className="trg-chart-inner">
                  <div className="trg-chart-top">
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div className="trg-card-title">Value Over Time</div>
                    </div>
                    <div className="trg-period-btns">
                      {["1M", "3M", "1Y", "3Y", "ALL"].map((r) => (
                        <button
                          key={r}
                          className={`trg-period-btn${activeRange === r ? " active" : ""}`}
                          onClick={() => setActiveRange(r)}
                        >
                          {r}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="trg-chart-legend">
                    <div className="trg-legend-item">
                      <div style={{ width: 16, height: 2, background: "var(--dash-blue)", borderRadius: 1 }} />
                      Your portfolio
                    </div>
                    {bmkName && (
                      <div className="trg-legend-item">
                        <div style={{ width: 16, height: 0, borderTop: "2px dashed var(--dash-gold)" }} />
                        {bmkName}
                      </div>
                    )}
                  </div>

                  <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
                    <div style={{ position: "absolute", inset: 0 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartPoints} margin={{ top: 6, right: 10, left: 0, bottom: 0 }}>
                          <defs>
                            <linearGradient id="areaGradCli" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="var(--dash-blue)" stopOpacity={0.16} />
                              <stop offset="100%" stopColor="var(--dash-blue)" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid stroke="rgba(255,255,255,0.04)" strokeDasharray="3 3" vertical={false} />
                          <XAxis
                            dataKey="as_of_date"
                            tickFormatter={(v, i) => chartPoints[i]?.label ?? v}
                            tick={{ fill: "rgba(255,255,255,0.25)", fontSize: 8, fontFamily: "var(--dash-font-m)" }}
                            axisLine={false} tickLine={false}
                            interval="preserveStartEnd"
                          />
                          <YAxis
                            tickFormatter={(v) => `${v}M`}
                            tick={{ fill: "rgba(255,255,255,0.25)", fontSize: 8, fontFamily: "var(--dash-font-m)" }}
                            axisLine={false} tickLine={false}
                            width={40} domain={["auto", "auto"]}
                          />
                          <Tooltip content={(props) => <ChartTooltip {...props} fmtMV={fmtMV} />} />
                          <Area
                            type="monotone" dataKey="value"
                            stroke="var(--dash-blue)" strokeWidth={1.8}
                            fill="url(#areaGradCli)" dot={false}
                            activeDot={{ r: 3, fill: "var(--dash-blue)" }}
                          />
                          <Line
                            type="monotone" dataKey="bmk" name={bmkName ?? "Benchmark"}
                            stroke="var(--dash-gold)" strokeWidth={1.5}
                            strokeDasharray="5 4" dot={false}
                            activeDot={{ r: 3, fill: "var(--dash-gold)" }}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div></div>,
            /* /chart tile — Value Over Time */

            /* Your Accounts */
            <div key="accounts" className="trg-tile"><div className="trg-card trg-table-card" style={{ height: "100%" }}>
                <div className="trg-table-inner">
                  <div className="trg-card-title">Your Accounts</div>
                  <div style={{ marginTop: 8 }}>
                    {brokerRows.map((b, i) => (
                      <div key={i} style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        fontSize: "0.72rem", color: "var(--dash-text2)", padding: "0.34rem 0",
                        borderBottom: i < brokerRows.length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none",
                      }}>
                        <span>{b.broker ?? "—"}</span>
                        <span style={{ fontFamily: "var(--dash-font-m)", color: "var(--dash-text)" }}>
                          {b.marketValue != null ? `${(b.marketValue / 1e6).toFixed(1)}M` : "—"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div></div>,
            /* /accounts tile */

            /* How spread out you are */
            <div key="spread" className="trg-tile"><div className="trg-card" style={{ height: "100%", display: "flex", flexDirection: "column" }}>
              <div className="trg-card-header">
                <div className="trg-card-title">How spread out you are</div>
              </div>
              <div style={{ padding: "0.85rem 1rem 1rem", display: "flex", flexDirection: "column", gap: "0.7rem" }}>
                {clientConcRows.filter((d) => d.max_weight != null).map((d) => {
                  const ratio = d.limit_value ? d.max_weight / d.limit_value : 0;
                  const c = ratio <= 0.95 ? "#22d47e" : ratio <= 1.05 ? "#C9A84C" : "#e05252";
                  const w = Math.min(100, Math.max(4, ratio * 100));
                  return (
                    <div key={d.category} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: "0.6rem", fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--dash-text2)", width: 96, flexShrink: 0 }}>{d.category}</span>
                      <div style={{ flex: 1, height: 6, borderRadius: 3, background: "rgba(255,255,255,0.05)", overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${w}%`, borderRadius: 3, background: c }} />
                      </div>
                      <span style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.68rem", width: 40, textAlign: "right", color: c }}>{fmtDec(d.max_weight)}%</span>
                    </div>
                  );
                })}
              </div>
            </div></div>,

            /* Money vs. Risk */
            <div key="money" className="trg-tile"><div className="trg-alloc-col-wrap" style={{ height: "100%" }}>
              <div className="trg-card-header">
                <div className="trg-card-title">Where your money is vs. where your risk is</div>
              </div>
              <div className="trg-alloc-body">
                <div style={{ display: "flex", flexDirection: "column", gap: "0.6rem", padding: "0.6rem 0.25rem 0", flex: 1 }}>
                  {(allocLevels?.all?.rows ?? []).map((row) => (
                    <div key={row.label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: "0.6rem", fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--dash-text2)", width: 96, flexShrink: 0 }}>{row.label}</span>
                      <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 3 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ flex: 1, height: 6, borderRadius: 3, background: "rgba(255,255,255,0.05)", overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${Math.min(100, Math.max(0, row.mv))}%`, borderRadius: 3, background: "#1e4a7a" }} />
                          </div>
                          <span style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.68rem", width: 40, textAlign: "right", color: "#4e8fdd" }}>{fmtDec(row.mv)}%</span>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ flex: 1, height: 6, borderRadius: 3, background: "rgba(255,255,255,0.05)", overflow: "hidden" }}>
                            <div style={{ height: "100%", width: `${Math.min(100, Math.max(0, row.var))}%`, borderRadius: 3, background: "#C9A84C" }} />
                          </div>
                          <span style={{ fontFamily: "var(--dash-font-m)", fontSize: "0.68rem", width: 40, textAlign: "right", color: "#C9A84C" }}>{fmtDec(row.var)}%</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div style={{ display: "flex", gap: 16, justifyContent: "center", fontSize: "0.6rem", color: "var(--dash-text2)", marginTop: 6 }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 9, height: 9, borderRadius: "50%", background: "#1e4a7a", display: "inline-block" }} />Your money</span>
                    <span style={{ display: "flex", alignItems: "center", gap: 5 }}><span style={{ width: 9, height: 9, borderRadius: "50%", background: "#C9A84C", display: "inline-block" }} />Your risk</span>
                  </div>
                </div>
              </div>
            </div></div>,
            /* /money tile */
          ];

          return (
            <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
              {isMobile ? (
                <div className="trg-mobile-stack">{clientTiles}</div>
              ) : (
                <ResponsiveGridLayout
                  className="trg-client-rgl"
                  layouts={{ lg: clientLayout }}
                  breakpoints={{ lg: 0 }}
                  cols={{ lg: 12 }}
                  rowHeight={28}
                  margin={[12, 12]}
                  isDraggable
                  isResizable
                  resizeHandles={["s", "e", "w", "n", "se", "sw", "ne", "nw"]}
                  draggableCancel="button,.trg-period-btn,input,select,textarea"
                  onLayoutChange={(l) => onClientLayoutChange(l)}
                >
                  {clientTiles}
                </ResponsiveGridLayout>
              )}
            </div>
          );
          })()}{/* /client draggable */}
        </main>

        {/* ── Fullscreen overlay ── */}
        {fsPanel && (
          <div className="trg-fs-overlay active" onClick={(e) => { if (e.target === e.currentTarget) closeFs(); }}>
            <div className="trg-fs-panel">
              <div className="trg-fs-header">
                <div className="trg-fs-title">{fsPanel.title}</div>
                <button className="trg-fs-close" onClick={closeFs}>✕ close</button>
              </div>
              <div className="trg-fs-body">
                {fsPanel.content}
              </div>
            </div>
          </div>
        )}

      </div>{/* /trg-shell */}
    </>
  );
}
